import React from 'react';
import styled, { css } from 'styled-components';
import CalculatorInput from './CalculatorInput';
import { useI18n } from '../i18n/index';
import { theme } from '../styles/theme';
import { fadeInUp, withMotion } from '../styles/animations';

const CalculatorForm = ({ form, onFieldChange, onReset, onFillExample, errors }) => {
  const { t } = useI18n();

  const getError = (field) => {
    if (!errors || !errors[field]) return null;
    if (field === 'startAltitude') return t('validation.startAltitudeTooLow');
    if (field === 'endAltitude') return t('validation.invalidAltitude');
    if (field === 'glideSlope') return t('validation.invalidGlideSlope');
    return t('validation.invalidNumber');
  };

  return (
    <Card>
      <CardTop>
        <SectionLabel>{t('form.title')}</SectionLabel>
        <ActionButtons>
          <ExampleButton
            type="button"
            data-testid="fill-example-button"
            onClick={onFillExample}
            title={t('form.fillExample')}
          >
            ↗ {t('form.fillExample')}
          </ExampleButton>
          <ResetButton
            type="button"
            data-testid="reset-button"
            onClick={onReset}
            title={t('form.reset')}
          >
            {t('form.reset')}
          </ResetButton>
        </ActionButtons>
      </CardTop>
      <Divider />

      <Group>
        <GroupLabel>{t('form.altitudeSection')}</GroupLabel>
        <TwoCol>
          <CalculatorInput
            id="startAltitude"
            label={`${t('form.startAltitude')} (${t('units.feet')})`}
            value={form.startAltitude}
            onChange={(e) => onFieldChange('startAltitude', e.target.value)}
            error={getError('startAltitude')}
            placeholder={t('form.startAltitudePlaceholder')}
            options={{
              numeral: true,
              numeralPositiveOnly: true,
              numeralIntegerScale: 5,
              numeralDecimalScale: 0,
              delimiter: ' ',
            }}
          />
          <CalculatorInput
            id="endAltitude"
            label={`${t('form.endAltitude')} (${t('units.feet')})`}
            value={form.endAltitude}
            onChange={(e) => onFieldChange('endAltitude', e.target.value)}
            error={getError('endAltitude')}
            placeholder={t('form.endAltitudePlaceholder')}
            options={{
              numeral: true,
              numeralPositiveOnly: true,
              numeralIntegerScale: 5,
              numeralDecimalScale: 0,
              delimiter: ' ',
            }}
          />
        </TwoCol>
      </Group>

      <Group>
        <GroupLabel>{t('form.speedSection')}</GroupLabel>
        <ThreeCol>
          <CalculatorInput
            id="startSpeed"
            label={`${t('form.groundSpeed')} (${t('units.knots')})`}
            value={form.startSpeed}
            onChange={(e) => onFieldChange('startSpeed', e.target.value)}
            placeholder={t('form.groundSpeedPlaceholder')}
            options={{
              numeral: true,
              numeralPositiveOnly: true,
              numeralIntegerScale: 4,
              numeralDecimalScale: 0,
              delimiter: ' ',
            }}
          />
          <CalculatorInput
            id="endSpeed"
            label={`${t('form.finalSpeed')} (${t('units.knots')})`}
            value={form.endSpeed}
            onChange={(e) => onFieldChange('endSpeed', e.target.value)}
            placeholder={t('form.finalSpeedPlaceholder')}
            options={{
              numeral: true,
              numeralPositiveOnly: true,
              numeralIntegerScale: 4,
              numeralDecimalScale: 0,
              delimiter: ' ',
            }}
          />
          <CalculatorInput
            id="wind"
            label={`${t('form.wind')} (${t('units.knots')})`}
            value={form.wind}
            onChange={(e) => onFieldChange('wind', e.target.value)}
            placeholder={t('form.windPlaceholder')}
            options={{
              numeral: true,
              numeralIntegerScale: 3,
              numeralDecimalScale: 0,
            }}
          />
        </ThreeCol>
      </Group>

      <Group>
        <GroupLabel>{t('form.profileSection')}</GroupLabel>
        <TwoCol>
          <CalculatorInput
            id="glideSlope"
            label={`${t('form.glideSlope')} (${t('units.degrees')})`}
            value={form.glideSlope}
            onChange={(e) => onFieldChange('glideSlope', e.target.value)}
            error={getError('glideSlope')}
            placeholder={t('form.glideSlopePlaceholder')}
            options={{
              numeral: true,
              numeralPositiveOnly: true,
              numeralIntegerScale: 1,
              numeralDecimalScale: 1,
            }}
          />
          <CalculatorInput
            id="verticalSpeed"
            label={`${t('form.verticalSpeed')} (${t('units.fpm')})`}
            value={form.verticalSpeed}
            onChange={(e) => onFieldChange('verticalSpeed', e.target.value)}
            placeholder={t('form.verticalSpeedPlaceholder')}
            options={{
              numeral: true,
              numeralPositiveOnly: true,
              numeralIntegerScale: 4,
              numeralDecimalScale: 0,
              delimiter: ' ',
            }}
          />
        </TwoCol>
        <Hint>{t('form.helperProfile')}</Hint>
      </Group>
    </Card>
  );
};

const Card = styled.section`
  background: ${theme.colors.surfaceElevated};
  border: 1px solid ${theme.colors.borderStrong};
  border-radius: ${theme.radius.xl};
  padding: ${theme.spacing.xl};
  box-shadow: ${theme.shadows.card};

  ${withMotion(css`
    animation: ${fadeInUp} 0.45s ease both;
    animation-delay: 0.05s;
  `)}

  @media (max-width: ${theme.breakpoints.mobile}) {
    padding: ${theme.spacing.lg} ${theme.spacing.md};
    border-radius: ${theme.radius.lg};
  }
`;

const CardTop = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: ${theme.spacing.md};
  flex-wrap: wrap;
`;

const ActionButtons = styled.div`
  display: flex;
  align-items: center;
  gap: ${theme.spacing.xs};
  flex-shrink: 0;
`;

const ExampleButton = styled.button`
  font-size: 11px;
  font-weight: 600;
  letter-spacing: 0.3px;
  color: ${theme.colors.skyBlue};
  padding: 4px 10px;
  border-radius: ${theme.radius.md};
  border: 1px solid rgba(59,130,246,0.3);
  background: ${theme.colors.skyBlueDim};
  transition: background 0.15s ease, border-color 0.15s ease;
  min-height: 28px;
  white-space: nowrap;

  &:hover {
    background: rgba(59,130,246,0.22);
    border-color: rgba(59,130,246,0.5);
  }

  &:focus-visible {
    outline: 2px solid ${theme.colors.skyBlue};
    outline-offset: 2px;
  }
`;

const SectionLabel = styled.h2`
  font-size: 11px;
  font-weight: 700;
  letter-spacing: 1.2px;
  text-transform: uppercase;
  color: ${theme.colors.textMuted};
`;

const ResetButton = styled.button`
  font-size: 11px;
  font-weight: 600;
  letter-spacing: 0.5px;
  color: ${theme.colors.textMuted};
  padding: 4px 10px;
  border-radius: ${theme.radius.md};
  border: 1px solid ${theme.colors.borderStrong};
  background: transparent;
  transition: color 0.15s ease, border-color 0.15s ease;
  min-height: 28px;

  &:hover {
    color: ${theme.colors.textSecondary};
    border-color: ${theme.colors.textMuted};
  }

  &:focus-visible {
    outline: 2px solid ${theme.colors.skyBlue};
    outline-offset: 2px;
  }
`;

const Divider = styled.hr`
  border: none;
  border-top: 1px solid ${theme.colors.borderSoft};
  margin: ${theme.spacing.md} 0 ${theme.spacing.lg};
`;

const Group = styled.div`
  & + & {
    margin-top: ${theme.spacing.xl};
  }
`;

const GroupLabel = styled.p`
  font-size: 11px;
  font-weight: 700;
  letter-spacing: 0.8px;
  color: ${theme.colors.skyBlue};
  margin-bottom: ${theme.spacing.sm};
  text-transform: uppercase;
`;

const TwoCol = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: ${theme.spacing.md};

  @media (max-width: ${theme.breakpoints.mobile}) {
    grid-template-columns: 1fr;
  }
`;

const ThreeCol = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr 1fr;
  gap: ${theme.spacing.md};

  @media (max-width: ${theme.breakpoints.tablet}) {
    grid-template-columns: 1fr 1fr;
  }

  @media (max-width: ${theme.breakpoints.mobile}) {
    grid-template-columns: 1fr;
  }
`;

const Hint = styled.p`
  margin-top: ${theme.spacing.sm};
  font-size: 11px;
  color: ${theme.colors.textMuted};
  font-style: italic;
  line-height: 1.5;
`;

export default CalculatorForm;
